import type { AiPhase, BannedTagInput, DeletionCandidate, DeletionReasonSummary, DeletionStatus } from './types'
import { normalizeTag } from './tags'

const PHASE_LABELS: Record<string, string> = {
  p1_uploads: 'User-Uploads',
  p2_priority: 'Priorität (tagme / Rating ?)',
  p3_video: 'Videos & GIFs',
  p4_no_ai: 'Bilder ohne AI-Tags',
  p5_rest: 'Neu-Tagging (alte AI-Tags)',
}

const REASON_LABELS: Record<string, string> = {
  minor_sexual: 'Minderjährige sexualisiert (Tags)',
  bestiality: 'Tierpornografie (Tags)',
  sexual_violence: 'Sexuelle Gewalt (Tags)',
  missing_file: 'Datei fehlt',
  ai_minor_sexual: 'Minderjährige sexualisiert (AI)',
  ai_bestiality: 'Tierpornografie (AI)',
  ai_sexual_violence: 'Sexuelle Gewalt (AI)',
}

const SOURCE_LABELS: Record<string, string> = {
  human_tags: 'Tags',
  ai: 'AI',
  scan: 'Scan',
}

export function phaseLabel(phase: string): string {
  return PHASE_LABELS[phase] ?? phase
}

export function reasonLabel(reason: string): string {
  return REASON_LABELS[reason] ?? reason
}

export function sourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source
}

export const DELETION_STATUSES: { value: DeletionStatus; label: string }[] = [
  { value: 'pending', label: 'Offen' },
  { value: 'approved', label: 'Freigegeben' },
  { value: 'rejected', label: 'Behalten' },
  { value: 'deleted', label: 'Gelöscht' },
]

const numberFormat = new Intl.NumberFormat('de-DE')
const decimalFormat = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

export function formatNumber(n: number): string {
  return numberFormat.format(Number.isFinite(n) ? n : 0)
}

/** `42,3 %` */
export function formatPercent(value: number): string {
  return `${decimalFormat.format(Number.isFinite(value) ? value : 0)} %`
}

export function formatRate(rate: number): string {
  return `${decimalFormat.format(Number.isFinite(rate) && rate > 0 ? rate : 0)} Bilder/s`
}

/** `1 Bild`, `1.234 Bilder` */
export function formatImages(n: number): string {
  return `${formatNumber(n)} ${n === 1 ? 'Bild' : 'Bilder'}`
}

/** Share of `done` in `total`, clamped to 0–100. */
export function percent(done: number, total: number): number {
  if (!(total > 0) || !Number.isFinite(done)) return 0
  return Math.min(100, Math.max(0, (done / total) * 100))
}

/** Seconds until `total` is reached at `rate` images per second, or null when unknown. */
export function etaSeconds(done: number, total: number, rate: number): number | null {
  if (!(rate > 0) || !Number.isFinite(rate)) return null
  return Math.max(0, total - done) / rate
}

/** `5 T 3 h`, `3 h 12 min`, `12 min`, `< 1 min`; `–` when unknown. */
export function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds) || seconds < 0) return '–'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 1) return '< 1 min'
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  const mins = minutes % 60
  if (days > 0) return `${days} T ${hours} h`
  if (hours > 0) return `${hours} h ${mins} min`
  return `${mins} min`
}

export interface ProgressTotals {
  total: number
  done: number
  errors: number
  /** Combined current rate of the phases that are still running. */
  rate: number
  percent: number
  eta: number | null
  finished: boolean
}

export function aggregateProgress(phases: AiPhase[]): ProgressTotals {
  let total = 0
  let done = 0
  let errors = 0
  let rate = 0
  for (const p of phases) {
    total += p.total || 0
    done += Math.min(p.done || 0, p.total || 0)
    errors += p.errors || 0
    if (!p.finished && p.rate > 0) rate += p.rate
  }
  const finished = phases.length > 0 && phases.every((p) => p.finished)
  return {
    total,
    done,
    errors,
    rate,
    percent: percent(done, total),
    eta: finished ? null : etaSeconds(done, total, rate),
    finished,
  }
}

/** `gerade eben`, `vor 45 s`, `vor 3 min`, `vor 2 h`, `vor 4 T`. */
export function formatRelative(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '–'
  const time = new Date(iso).getTime()
  if (Number.isNaN(time)) return '–'
  const seconds = Math.round((now - time) / 1000)
  if (seconds < 10) return 'gerade eben'
  if (seconds < 60) return `vor ${seconds} s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `vor ${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `vor ${hours} h`
  return `vor ${Math.floor(hours / 24)} T`
}

export type ReasonCounts = Omit<DeletionReasonSummary, 'reason'>

/** Counts for one reason, or summed over all reasons when `reason` is empty. */
export function reasonCounts(reasons: DeletionReasonSummary[], reason: string): ReasonCounts {
  const out: ReasonCounts = { pending: 0, approved: 0, rejected: 0, deleted: 0 }
  for (const r of reasons) {
    if (reason && r.reason !== reason) continue
    out.pending += r.pending
    out.approved += r.approved
    out.rejected += r.rejected
    out.deleted += r.deleted
  }
  return out
}

/** Image count of a banned tag; `–` when the backend could not count. */
export function formatImageCount(n: number | null | undefined): string {
  return typeof n === 'number' && Number.isFinite(n) ? formatNumber(n) : '–'
}

/**
 * Validates the ban form. The tag is normalised like everywhere else (the
 * server does the same); an empty note is left out.
 */
export function buildBanInput(
  rawTag: string,
  includeAi: boolean,
  rawNote: string,
): { input: BannedTagInput; error?: undefined } | { input?: undefined; error: string } {
  if (!rawTag.trim()) return { error: 'Bitte einen Tag eingeben.' }
  const tag = normalizeTag(rawTag)
  if (!tag) return { error: 'Dieser Tag ist ungültig.' }
  const note = rawNote.trim()
  return { input: note ? { tag, include_ai: includeAi, note } : { tag, include_ai: includeAi } }
}

/** Items across pages, first occurrence wins (cursor pages may overlap after decisions). */
export function dedupeCandidates(pages: { items: DeletionCandidate[] | null }[]): DeletionCandidate[] {
  const seen = new Set<string>()
  const out: DeletionCandidate[] = []
  for (const page of pages) {
    for (const item of page.items ?? []) {
      if (seen.has(item.image_id)) continue
      seen.add(item.image_id)
      out.push(item)
    }
  }
  return out
}

/** Flat, printable entries of a candidate's `details` object. */
export function detailEntries(details: Record<string, unknown> | null | undefined): [string, string][] {
  if (!details || typeof details !== 'object') return []
  const out: [string, string][] = []
  for (const [key, value] of Object.entries(details)) {
    if (value === null || value === undefined || value === '') continue
    if (typeof value === 'number') out.push([key, Number.isInteger(value) ? String(value) : value.toFixed(3)])
    else if (typeof value === 'string' || typeof value === 'boolean') out.push([key, String(value)])
    else if (Array.isArray(value)) out.push([key, value.map(String).join(', ')])
    else out.push([key, JSON.stringify(value)])
  }
  return out
}
